import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MembershipTierService } from '../../core/api-services/membership-tier.service';

/**
 * Subscription Checkout Return (Task Group 8)
 *
 * Stripe-hosted Checkout redirects here after the user pays for the
 * annual Pro subscription. This is the Hub's own default web return path
 * for the 'subscription' flow (`buildReturnUrls`'s WEB_DEFAULT_PATHS in
 * user-payments-controller.ts) — not passed explicitly by
 * SubscriptionBillingService, just relied upon.
 *
 * Unlike the Map Analysis checkout return, there's no separate
 * "from-checkout-session" finalize call to make here: subscription state
 * is driven by the `customer.subscription.*` webhook, and
 * `GET /payments/subscriptions/status` (what `fetchStatus()` calls)
 * already self-heals against live Stripe data when the local row looks
 * stale (`MembershipTierService.getReconciledAccount` on the Hub side) —
 * so refreshing the signal here is sufficient; no separate verification
 * step needed.
 *
 * Route: /billing/subscription/return
 */
@Component({
  selector: 'app-subscription-checkout-return',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="container py-5 mw-560px">
      <div class="card shadow-sm">
        <div class="card-body p-4 text-center">
          <ng-container *ngIf="state() === 'verifying'">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Verifying...</span>
            </div>
            <h4 class="mb-2">Confirming your subscription…</h4>
            <p class="text-muted mb-0">This will only take a moment.</p>
          </ng-container>

          <ng-container *ngIf="state() === 'cancelled'">
            <i class="bi bi-x-circle text-warning fs-2-5rem"></i>
            <h4 class="mt-3 mb-2">Checkout cancelled</h4>
            <p class="text-muted mb-3">No payment was charged. You can start over whenever you're ready.</p>
            <a routerLink="/billing/upgrade" class="btn btn-primary">Back to Upgrade to Pro</a>
          </ng-container>

          <ng-container *ngIf="state() === 'pending'">
            <i class="bi bi-hourglass-split text-primary fs-2-5rem"></i>
            <h4 class="mt-3 mb-2">Still confirming…</h4>
            <p class="text-muted mb-3">
              Your payment may still be processing on Stripe's side. This usually resolves within a minute —
              try refreshing.
            </p>
            <button class="btn btn-outline-primary" (click)="checkStatus()">Refresh</button>
          </ng-container>

          <ng-container *ngIf="state() === 'error'">
            <i class="bi bi-exclamation-triangle text-danger fs-2-5rem"></i>
            <h4 class="mt-3 mb-2">Something went wrong</h4>
            <p class="text-muted mb-3">{{ errorMessage() }}</p>
            <a routerLink="/billing/upgrade" class="btn btn-outline-primary">Back to Upgrade to Pro</a>
          </ng-container>
        </div>
      </div>
    </div>
  `,
})
export class SubscriptionCheckoutReturnComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private membershipTierService = inject(MembershipTierService);

  state = signal<'verifying' | 'cancelled' | 'pending' | 'error'>('verifying');
  errorMessage = signal('');

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('cancelled')) {
      this.state.set('cancelled');
      return;
    }

    await this.checkStatus();
  }

  // Extracted from ngOnInit so the pending state's Refresh button can re-run
  // it directly — a routerLink back to this same route is a same-URL
  // navigation, which the router's default onSameUrlNavigation:'ignore'
  // silently no-ops (Copilot catch on PR #108).
  async checkStatus(): Promise<void> {
    this.state.set('verifying');
    // fetchStatus() never throws (it catches internally and fails closed to
    // Core) — a try/catch here would never fire, silently mislabeling a real
    // fetch failure as "still pending" instead of "couldn't confirm". Read
    // MembershipTierService's own lastFetchFailed signal instead (Copilot
    // catch on PR #108).
    await this.membershipTierService.fetchStatus();

    if (this.membershipTierService.lastFetchFailed()) {
      this.state.set('error');
      this.errorMessage.set('Could not confirm your subscription with the server. Please try refreshing.');
    } else if (this.membershipTierService.isPro()) {
      this.router.navigate(['/billing']);
    } else {
      // Webhook + reconciliation both haven't caught up yet — rare, but
      // possible under real-world delivery delays. Not an error: the
      // payment almost certainly succeeded (Stripe only redirects here on
      // success), it just hasn't landed in our system as Pro yet.
      this.state.set('pending');
    }
  }
}
